import type {
  ProgramPageBlock,
  ProgramPageBlockType,
  ProgramPageDocument,
  ProgramPageProgram,
  ProgramPageTheme,
  ProgramPageTierItem,
} from "@/types/programPage";

export const newId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/** Icons handed out rung by rung, bottom to top. */
const RUNG_ICONS = ["Sprout", "Flame", "Trophy", "Crown", "Gem", "Diamond", "Rocket", "Star"];

export function formatCommission(
  program: Pick<ProgramPageProgram, "commissionType"> | null | undefined,
  value: number
): string {
  const clean = Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, "");
  return program?.commissionType === "FIXED" ? `$${clean}` : `${clean}%`;
}

export function newTierItem(overrides: Partial<ProgramPageTierItem> = {}): ProgramPageTierItem {
  return {
    id: newId(),
    tierId: null,
    name: "",
    badge: "",
    tagline: "",
    commissionLabel: "",
    icon: null,
    color: null,
    imageUrl: null,
    requirementsTitle: "How to get here",
    requirements: [],
    rewardsTitle: "What you unlock",
    rewards: [],
    ...overrides,
  };
}

/**
 * One card per live rung, linked, so name and rate follow the ladder. With no
 * ladder yet the page still needs to look like something, so three display-
 * only rungs stand in until the brand sets real tiers.
 */
export function tierItemsFor(
  program: ProgramPageProgram | null,
  rungColors: string[]
): ProgramPageTierItem[] {
  const color = (i: number) => rungColors[i % rungColors.length] ?? null;
  const icon = (i: number) => ({
    kind: "lucide" as const,
    value: RUNG_ICONS[i % RUNG_ICONS.length],
  });

  if (program?.tiers.length) {
    return program.tiers.map((tier, i) =>
      newTierItem({
        tierId: tier.id,
        icon: icon(i),
        color: color(i),
        rewards: [
          `${formatCommission(program, tier.commissionValue)} commission on every sale`,
          ...(tier.perks ? [tier.perks] : []),
        ],
      })
    );
  }

  return [
    { name: "Starter", rate: "10%", rewards: ["10% commission", "Welcome gift"] },
    { name: "Creator", rate: "15%", rewards: ["15% commission", "Early access to drops"] },
    { name: "Elite", rate: "20%", rewards: ["20% commission", "Paid campaign opportunities"] },
  ].map((rung, i) =>
    newTierItem({
      name: rung.name,
      commissionLabel: rung.rate,
      icon: icon(i),
      color: color(i),
      rewards: rung.rewards,
    })
  );
}

/** A fresh block of `type`, with enough sample copy to show what it is for. */
export function createBlock(
  type: ProgramPageBlockType,
  context: { program: ProgramPageProgram | null; rungColors?: string[] } = { program: null }
): ProgramPageBlock {
  const id = newId();
  switch (type) {
    case "hero":
      return {
        id,
        type,
        layout: "centered",
        eyebrow: "Creator program",
        title: "Earn more as you grow",
        subtitle: "Every sale moves you up. Every tier pays better.",
        imageUrl: null,
        cta: { label: "Join the program", action: "join", url: null },
      };
    case "tiers":
      return {
        id,
        type,
        heading: "The tiers",
        subheading: "",
        programId: null,
        layout: "cards",
        showProgress: true,
        showThresholds: true,
        items: tierItemsFor(context.program, context.rungColors ?? []),
      };
    case "features":
      return {
        id,
        type,
        heading: "Why join",
        subheading: "",
        columns: 3,
        items: [
          { id: newId(), icon: { kind: "lucide", value: "BadgePercent" }, title: "Real commission", text: "Paid on every order your audience places." },
          { id: newId(), icon: { kind: "lucide", value: "Gift" }, title: "Free product", text: "Earn pieces as you climb." },
          { id: newId(), icon: { kind: "lucide", value: "Users" }, title: "A real team", text: "Direct access to the people behind the brand." },
        ],
      };
    case "steps":
      return {
        id,
        type,
        heading: "How it works",
        subheading: "",
        items: [
          { id: newId(), title: "Share your code", text: "Post it where your audience already is." },
          { id: newId(), title: "Make sales", text: "Every order counts toward your next tier." },
          { id: newId(), title: "Level up", text: "Reach a tier and your rate rises on every sale after it." },
        ],
      };
    case "stats":
      return {
        id,
        type,
        items: [
          { id: newId(), value: "20%", label: "Top commission" },
          { id: newId(), value: "3", label: "Tiers to climb" },
          { id: newId(), value: "$0", label: "Cost to join" },
        ],
      };
    case "text":
      return { id, type, heading: "", body: "Write anything here.", align: "left" };
    case "image":
      return { id, type, imageUrl: null, alt: "", caption: "" };
    case "faq":
      return {
        id,
        type,
        heading: "Questions",
        items: [
          { id: newId(), question: "How do I move up a tier?", answer: "Tiers are based on your total sales. Once you cross a tier's threshold, its commission applies to every sale after that." },
          { id: newId(), question: "When do I get paid?", answer: "Commissions are paid out from your dashboard once they clear." },
        ],
      };
    case "cta":
      return {
        id,
        type,
        title: "Ready to start?",
        subtitle: "",
        cta: { label: "Apply now", action: "join", url: null },
      };
  }
}

export interface ProgramPageTemplate {
  key: string;
  name: string;
  description: string;
  /** Tier accent colours, bottom rung to top. Empty = every rung in the brand accent. */
  rungColors: string[];
  tierLayout: "cards" | "ladder" | "table";
  blocks: ProgramPageBlockType[];
}

/** Every template wears the app theme; they differ in structure, not skin. */
export const APP_THEME: ProgramPageTheme = {
  colors: { background: null, surface: null, text: null, accent: null },
  sectionNumbers: false,
};

export const PROGRAM_PAGE_TEMPLATES: ProgramPageTemplate[] = [
  {
    key: "classic",
    name: "Classic",
    description: "Headline, how it works, the tiers, then questions.",
    rungColors: [],
    tierLayout: "cards",
    blocks: ["hero", "steps", "tiers", "features", "faq", "cta"],
  },
  {
    key: "numbers",
    name: "Numbers first",
    description: "Leads with a stat row, then the tiers as cards.",
    rungColors: [],
    tierLayout: "cards",
    blocks: ["hero", "stats", "tiers", "features", "faq", "cta"],
  },
  {
    key: "ladder",
    name: "Ladder",
    description: "The tiers as a vertical climb, with the rules underneath.",
    rungColors: [],
    tierLayout: "ladder",
    blocks: ["hero", "tiers", "steps", "text", "cta"],
  },
  {
    key: "compact",
    name: "Compact",
    description: "A short page: intro, a comparison table, questions.",
    rungColors: [],
    tierLayout: "table",
    blocks: ["hero", "tiers", "faq", "cta"],
  },
];

export function buildFromTemplate(
  template: ProgramPageTemplate,
  context: { brandName: string | null; program: ProgramPageProgram | null }
): ProgramPageDocument {
  const blocks = template.blocks.map((type) => {
    const block = createBlock(type, { program: context.program, rungColors: template.rungColors });
    if (block.type === "hero" && context.brandName) {
      block.eyebrow = `${context.brandName} creator program`;
    }
    if (block.type === "tiers") block.layout = template.tierLayout;
    return block;
  });
  return { version: 1, theme: structuredClone(APP_THEME), blocks };
}

export function blankDocument(): ProgramPageDocument {
  return { version: 1, theme: structuredClone(APP_THEME), blocks: [] };
}
